import '../styles/header.scss';

// =============================================================================
//  HEADER – frontend-beteende
// -----------------------------------------------------------------------------
//  Frontend-ägt beteende. Hakar i data-attributen från kontraktet:
//    [data-header]           – rot
//    [data-header-toggle]    – öppna-knapp
//    [data-header-overlay]   – fullscreen-panel
//    [data-header-close]     – stäng-knapp
//  Importeras som webpack-entry -> assets/front/header/header.js
// =============================================================================

const HIDE_THRESHOLD = 40;
const SHOW_THRESHOLD = 25;

const updateToolbarOffset = () => {
    const adminBar = document.getElementById('wpadminbar');
    if (!adminBar) {
        return;
    }

    const visibleHeight = Math.max(0, Math.round(adminBar.getBoundingClientRect().bottom));
    document.documentElement.style.setProperty('--toolbar-height', `${visibleHeight}px`);
};

const initToolbarOffset = () => {
    const adminBar = document.getElementById('wpadminbar');
    if (!adminBar) {
        return;
    }

    let ticking = false;
    const onScrollOrResize = () => {
        if (!ticking) {
            window.requestAnimationFrame(() => {
                updateToolbarOffset();
                ticking = false;
            });
            ticking = true;
        }
    };

    updateToolbarOffset();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize, { passive: true });
};

const initSmartSticky = (header) => {
    let lastScrollY = Math.max(0, window.scrollY);
    let accumulatedDown = 0;
    let accumulatedUp = 0;
    let ticking = false;

    const update = () => {
        if (
            header.classList.contains('is-open') ||
            header.classList.contains('is-search-open') ||
            document.body.classList.contains('is-header-open')
        ) {
            header.classList.remove('is-hidden');
            lastScrollY = Math.max(0, window.scrollY);
            accumulatedDown = 0;
            accumulatedUp = 0;
            ticking = false;
            return;
        }

        const currentY = Math.max(0, window.scrollY);
        const delta = currentY - lastScrollY;
        const headerHeight = header.offsetHeight || 140;

        if (currentY <= 10) {
            if (header.classList.contains('is-hidden')) {
                header.classList.remove('is-hidden');
            }
            if (header.classList.contains('is-pinned')) {
                header.classList.remove('is-pinned');
            }
            accumulatedDown = 0;
            accumulatedUp = 0;
        } else {
            if (delta > 0) {
                accumulatedUp = 0;
                accumulatedDown += delta;

                if (accumulatedDown >= HIDE_THRESHOLD && currentY > headerHeight) {
                    if (!header.classList.contains('is-hidden')) {
                        header.classList.add('is-hidden');
                    }
                }
            } else if (delta < 0) {
                accumulatedDown = 0;
                accumulatedUp += Math.abs(delta);

                if (accumulatedUp >= SHOW_THRESHOLD) {
                    if (!header.classList.contains('is-pinned')) {
                        header.classList.add('is-hidden');
                        header.classList.add('is-pinned');
                        void header.offsetHeight;
                    }
                    if (header.classList.contains('is-hidden')) {
                        header.classList.remove('is-hidden');
                    }
                }
            }
        }

        const isScrolled = currentY > 0;
        if (header.classList.contains('is-scrolled') !== isScrolled) {
            header.classList.toggle('is-scrolled', isScrolled);
        }

        lastScrollY = currentY;
        ticking = false;
    };

    window.addEventListener(
        'scroll',
        () => {
            if (!ticking) {
                window.requestAnimationFrame(update);
                ticking = true;
            }
        },
        { passive: true },
    );

    update();
};

const initOverlayAndSearch = (header) => {
    const menuToggle = header.querySelector('[data-header-toggle]');
    const menuOverlay = header.querySelector('[data-header-overlay]');
    const menuClose = header.querySelector('[data-header-close]');

    const searchToggle = header.querySelector('[data-header-search-toggle]');
    const searchOverlay = header.querySelector('[data-header-search-overlay]');
    const searchInput = searchOverlay?.querySelector('.f-header__search-input');
    const searchClear = searchOverlay?.querySelector('[data-header-search-clear]');
    const searchClose = header.querySelector('[data-header-search-close]');

    let savedScrollY = 0;
    let isScrollLocked = false;

    const updateClearButton = () => {
        if (!searchClear || !searchInput) {
            return;
        }
        const hasText = searchInput.value.length > 0;
        searchClear.toggleAttribute('hidden', !hasText);
    };

    searchInput?.addEventListener('input', updateClearButton);

    searchClear?.addEventListener('click', () => {
        if (searchInput) {
            searchInput.value = '';
            searchInput.focus();
            updateClearButton();
        }
    });

    const lockScroll = () => {
        if (!isScrollLocked) {
            savedScrollY = window.scrollY;
            document.documentElement.classList.add('is-header-open');
            document.body.classList.add('is-header-open');
            document.body.style.top = `-${savedScrollY}px`;
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            isScrollLocked = true;
            updateToolbarOffset();
        }
    };

    const unlockScroll = () => {
        if (isScrollLocked) {
            document.documentElement.classList.remove('is-header-open');
            document.body.classList.remove('is-header-open');
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            window.scrollTo({ top: savedScrollY, behavior: 'instant' });
            isScrollLocked = false;
            updateToolbarOffset();
        }
    };

    const setMenuOpen = (open) => {
        if (!menuToggle || !menuOverlay) {
            return;
        }

        if (open) {
            header.classList.remove('is-hidden');
            if (header.classList.contains('is-search-open')) {
                setSearchOpen(false, false);
            }
            lockScroll();
            menuOverlay.scrollTop = 0;
            menuOverlay.style.setProperty('--header-height', `${header.offsetHeight}px`);
        } else if (!header.classList.contains('is-search-open')) {
            unlockScroll();
        }

        menuToggle.setAttribute('aria-expanded', String(open));
        menuOverlay.toggleAttribute('hidden', !open);
        header.classList.toggle('is-open', open);

        if (open) {
            menuOverlay.querySelector('a, button')?.focus({ preventScroll: true });
        } else if (!header.classList.contains('is-search-open')) {
            menuToggle.focus({ preventScroll: true });
        }
    };

    const setSearchOpen = (open, manageScroll = true) => {
        if (!searchToggle || !searchOverlay) {
            return;
        }

        if (open) {
            header.classList.remove('is-hidden');
            if (header.classList.contains('is-open')) {
                setMenuOpen(false);
            }
            if (manageScroll) {
                lockScroll();
            }
            searchOverlay.scrollTop = 0;
            searchOverlay.style.setProperty('--header-height', `${header.offsetHeight}px`);
        } else if (manageScroll && !header.classList.contains('is-open')) {
            unlockScroll();
        }

        searchToggle.setAttribute('aria-expanded', String(open));
        searchOverlay.toggleAttribute('hidden', !open);
        header.classList.toggle('is-search-open', open);
        updateClearButton();

        if (open) {
            searchInput?.focus({ preventScroll: true });
            searchInput?.select();
        } else if (manageScroll && !header.classList.contains('is-open')) {
            searchToggle.focus({ preventScroll: true });
        }
    };

    menuToggle?.addEventListener('click', () => {
        setMenuOpen(menuToggle.getAttribute('aria-expanded') !== 'true');
    });

    menuClose?.addEventListener('click', () => setMenuOpen(false));

    searchToggle?.addEventListener('click', () => {
        setSearchOpen(searchToggle.getAttribute('aria-expanded') !== 'true');
    });

    searchClose?.addEventListener('click', () => setSearchOpen(false));

    searchOverlay?.addEventListener('click', (event) => {
        if (event.target === searchOverlay || event.target.classList.contains('f-header__search-overlay-inner')) {
            setSearchOpen(false);
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            if (header.classList.contains('is-search-open')) {
                setSearchOpen(false);
            } else if (header.classList.contains('is-open')) {
                setMenuOpen(false);
            }
        }
    });

    window.addEventListener(
        'resize',
        () => {
            const h = `${header.offsetHeight}px`;
            if (header.classList.contains('is-open')) {
                menuOverlay?.style.setProperty('--header-height', h);
            }
            if (header.classList.contains('is-search-open')) {
                searchOverlay?.style.setProperty('--header-height', h);
            }
        },
        { passive: true },
    );
};

const initMegaAccordion = (header) => {
    const cols = header.querySelectorAll('.f-header__mega-col');

    cols.forEach((col) => {
        const toggle = col.querySelector('[data-header-mega-toggle]');
        const content = col.querySelector('[data-header-mega-content]');
        const titleLink = col.querySelector('.f-header__mega-title a');

        if (!toggle || !content) {
            return;
        }

        const setOpen = (open) => {
            toggle.setAttribute('aria-expanded', String(open));
            col.classList.toggle('is-expanded', open);
            content.classList.toggle('is-expanded', open);
            const titleText = col.querySelector('.f-header__mega-title')?.textContent?.trim() || '';
            toggle.setAttribute(
                'aria-label',
                open ? `${titleText}: Dölj undersidor` : `${titleText}: Visa undersidor`,
            );
        };

        toggle.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const isOpen = toggle.getAttribute('aria-expanded') === 'true';
            setOpen(!isOpen);
        });

        if (!titleLink || titleLink.getAttribute('href') === '#' || !titleLink.getAttribute('href')) {
            const headerRow = col.querySelector('.f-header__mega-header');
            headerRow?.addEventListener('click', (event) => {
                if (window.matchMedia('(max-width: 1024px)').matches) {
                    if (event.target.closest('[data-header-mega-toggle]')) {
                        return;
                    }
                    event.preventDefault();
                    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
                    setOpen(!isOpen);
                }
            });
            headerRow?.style.setProperty('cursor', 'pointer');
        }
    });
};

const header = document.querySelector('[data-header]');

if (header) {
    const updateHeaderHeight = () => {
        document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight, { passive: true });

    if (window.ResizeObserver) {
        new ResizeObserver(updateHeaderHeight).observe(header);
    }

    initToolbarOffset();
    initSmartSticky(header);
    initOverlayAndSearch(header);
    initMegaAccordion(header);
}
